// SalaryTemplate
import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { SALARY_TEMPLATE } from "../../constants/api";
import { useNavigate } from "react-router-dom";
import { FileText, Edit } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";

const SalaryTemplate = () => {
    const [SalaryTemplateData, setSalaryTemplateData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const navigate = useNavigate();

    const {
        add: addPermission,
        edit: editPermission,
        draft: draftPermission,
        active: isActive
    } = checkUserPermissionFunc("configuration", "Salary template", "SubMenu");

    const SalaryTemplateColumns = [
        {
            accessor: "code",
            Header: "Template Code",
            sortable: true,
            filterable: true,
            width: "120px",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "salary_template_name",
            Header: "Salary Template Name",
            sortable: true,
            filterable: true,
            width: "200px",
            Cell: ({ value }) => value ?? "-",
        },
        {
            accessor: "department_name",
            Header: "Department",
            sortable: true,
            filterable: true,
            width: "150px",
            Cell: ({ value, row }) => {
                if (row?.department_name) return row.department_name;
                const names = (row?.departments || [])
                    .map((dept) => dept?.department_name)
                    .filter(Boolean);
                if (names.length) return names.join(", ");
                return row?.department?.department_name || "-";
            },
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: true,
            filterable: true,
            width: "100px",
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                width: "80px",
                Cell: ({ row }) => {
                    const handleOpenEdit = () => {
                        navigate('/configuration/salarytemplate/add', {
                            state: {
                                fromEdit: true,
                                editDetails: row,
                                isActive: isActive
                            }
                        });
                    };

                    return (
                        <div className="flex items-center justify-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenEdit}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        </div>
                    );
                },
            }
        ] : [])
    ];

    const handleAddTemplate = () => {
        navigate('/configuration/salarytemplate/add', {
            state: {
                fromEdit: false,
                editDetails: {},
                isActive: isActive
            }
        });
    };

    return (
        <ContainerCard>
            {/* Header */}
            <ModuleTitleCard
                Icon={FileText}
                title={'Salary Template'}
                subtitle="Define and manage salary structures and compensation templates"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={handleAddTemplate}
                buttonName={'Add Salary Template'}
            />

            <Reactable
                columns={SalaryTemplateColumns}
                data={SalaryTemplateData}
                setData={setSalaryTemplateData}
                setTotalCount={setTotalRecords}
                url={SALARY_TEMPLATE}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={false}
            />
        </ContainerCard>
    );
};

export default SalaryTemplate;